import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDownRight, ArrowUpRight, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { ConfirmDialog } from "../components/Common";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";

export default function Kategori() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, cat: null, type: "income" });
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const load = useCallback(() => {
    api.get("/categories")
      .then((r) => setCategories(r.data))
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const openAdd = (type) => {
    setName("");
    setModal({ open: true, cat: null, type });
  };

  const openEdit = (cat) => {
    setName(cat.name);
    setModal({ open: true, cat, type: cat.type });
  };

  const save = async () => {
    if (!name.trim()) {
      toast.error("Nama kategori wajib diisi");
      return;
    }
    setSaving(true);
    try {
      if (modal.cat) {
        await api.put(`/categories/${modal.cat.id}`, { name, type: modal.cat.type, active: modal.cat.active });
        toast.success("Kategori diperbarui");
      } else {
        await api.post("/categories", { name, type: modal.type, active: true });
        toast.success("Kategori ditambahkan");
      }
      setModal((m) => ({ ...m, open: false }));
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (cat, active) => {
    try {
      await api.put(`/categories/${cat.id}`, { name: cat.name, type: cat.type, active });
      toast.success(`Kategori "${cat.name}" ${active ? "diaktifkan" : "dinonaktifkan"}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const doDelete = async () => {
    setDeleteLoading(true);
    try {
      await api.delete(`/categories/${deleting.id}`);
      toast.success("Kategori dihapus");
      setDeleting(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setDeleteLoading(false);
    }
  };

  const renderList = (type, title, Icon, colorClass) => {
    const list = categories.filter((c) => c.type === type);
    return (
      <div className="bg-[#1E293B] border border-slate-800 rounded-xl shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <div className="flex items-center gap-2">
            <div className={`p-2 rounded-lg ${colorClass}`}><Icon className="w-4 h-4" /></div>
            <div>
              <h3 className="text-base font-bold text-slate-100">{title}</h3>
              <p className="text-xs text-slate-400">{list.length} kategori</p>
            </div>
          </div>
          <button
            data-testid={`btn-add-category-${type}`}
            onClick={() => openAdd(type)}
            className="bg-slate-900 text-white hover:bg-slate-800 font-medium px-3 py-2 rounded-lg transition-colors flex items-center gap-2 text-xs shadow-sm"
          >
            <Plus className="w-3.5 h-3.5" /> Tambah
          </button>
        </div>
        <div className="divide-y divide-slate-800">
          {list.map((c) => (
            <div key={c.id} data-testid={`row-category-${c.id}`} className="flex items-center justify-between px-5 py-3 hover:bg-slate-800/40">
              <div className="flex items-center gap-3">
                <span className={`text-sm font-medium ${c.active ? "text-slate-100" : "text-slate-400 line-through"}`}>{c.name}</span>
                {!c.active && <span className="text-[10px] font-bold uppercase tracking-wider bg-slate-800 text-slate-400 rounded-full px-2 py-0.5">Nonaktif</span>}
              </div>
              <div className="flex items-center gap-2">
                <Switch data-testid={`switch-category-${c.id}`} checked={c.active} onCheckedChange={(v) => toggleActive(c, v)} />
                <button data-testid={`btn-edit-category-${c.id}`} onClick={() => openEdit(c)} className="p-1.5 rounded-md hover:bg-slate-800/40 text-slate-400 hover:text-blue-300"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`btn-delete-category-${c.id}`} onClick={() => setDeleting(c)} className="p-1.5 rounded-md hover:bg-rose-500/15 text-slate-400 hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
          {list.length === 0 && <p className="px-5 py-6 text-sm text-slate-400 text-center">Belum ada kategori</p>}
        </div>
      </div>
    );
  };

  if (loading)
    return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-4" data-testid="kategori-page">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {renderList("income", "Kategori Pemasukan", ArrowDownRight, "bg-emerald-500/15 text-emerald-400")}
        {renderList("expense", "Kategori Pengeluaran", ArrowUpRight, "bg-rose-500/15 text-rose-400")}
      </div>

      <Dialog open={modal.open} onOpenChange={(o) => setModal((m) => ({ ...m, open: o }))}>
        <DialogContent data-testid="modal-category-form" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{modal.cat ? "Edit Kategori" : `Tambah Kategori ${modal.type === "income" ? "Pemasukan" : "Pengeluaran"}`}</DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5 py-2">
            <Label htmlFor="cat-name">Nama Kategori</Label>
            <Input id="cat-name" data-testid="input-category-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Contoh: Operasional" onKeyDown={(e) => e.key === "Enter" && save()} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal((m) => ({ ...m, open: false }))}>Batal</Button>
            <Button data-testid="btn-category-save" className="bg-slate-900 hover:bg-slate-800 text-white" onClick={save} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Simpan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(o) => !o && setDeleting(null)}
        title="Hapus Kategori?"
        description={deleting ? `Kategori "${deleting.name}" akan dihapus. Kategori yang masih digunakan transaksi tidak dapat dihapus.` : ""}
        onConfirm={doDelete}
        loading={deleteLoading}
      />
    </div>
  );
}
